import React from "react";
import {
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Button,
  Link,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { grey } from "@mui/material/colors";
import { MessageCircleIcon, MailCheckIcon } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  avatar: {
    color: "#666",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    width: 40,
    height: 40,
    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      transform: "translateY(-4px) scale(1.1)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
  },
  listItem: {
    marginLeft: 0,
    paddingLeft: 0,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: "10px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
      transform: "translateX(5px)",
      "& $avatar": {
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      },
      "& $contactIcon": {
        color: "#fff",
      },
    },
  },
  icon: {
    height: 22,
    width: 22,
    transition: "transform 0.3s ease",
  },
  contactIcon: {
    color: grey[700],
    transition: "all 0.3s ease",
  },
  contactLink: {
    color: "#555",
    textDecoration: "none",
    fontWeight: 500,
    transition: "color 0.3s ease",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));

export const SocialMedia = () => {
  const classes = useStyles();
  const items = [
    {
      alt: "facebook",
      href: "https://www.facebook.com/dataintegratedltd/",
      icon: <FacebookIcon className={classes.icon} />,
    },
    {
      alt: "twitter",
      href: "https://twitter.com/DataIntegrated",
      icon: <TwitterIcon className={classes.icon} />,
    },
    {
      alt: "linkedin",
      href: "https://www.linkedin.com/company/27253906/",
      icon: <LinkedInIcon className={classes.icon} />,
    },
    {
      alt: "instagram",
      href: "https://www.instagram.com/dataintegratedlimited/",
      icon: <InstagramIcon className={classes.icon} />,
    },
    {
      alt: "youtube",
      href: "https://www.youtube.com/channel/UCjpZhDUUwjG5BJjxzjHMrdg",
      icon: <YouTubeIcon className={classes.icon} />,
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {items.map((item, index) => (
        <Grid item key={index}>
          <Avatar
            component={Link}
            alt={item.alt}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.avatar}
            aria-label={`Visit our ${item.alt} page`}
          >
            {item.icon}
          </Avatar>
        </Grid>
      ))}
    </Grid>
  );
};

const Main = ({ isAddressVisible, isSocialMediaVisible }) => {
  const classes = useStyles();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={12} sm={12} xl={12} md={12}>
        {isAddressVisible ? (
          <List>
            <ListItem className={classes.listItem}>
              <ListItemAvatar>
                <Avatar className={classes.avatar}>
                  <PhoneIcon className={classes.contactIcon} />
                </Avatar>
              </ListItemAvatar>
              <div>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" color="textSecondary">
                      Call Us
                    </Typography>
                  }
                  secondary={
                    <Typography
                      component="a"
                      href="tel:+254711520705"
                      className={classes.contactLink}
                    >
                      +254 711 520 705
                    </Typography>
                  }
                />

                <ListItemText
                  secondary={
                    <Typography
                      component="a"
                      href="tel:+254742250992"
                      className={classes.contactLink}
                    >
                      +254 742 250 992
                    </Typography>
                  }
                />
              </div>
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemAvatar>
                <Avatar className={classes.avatar}>
                  <LocationOnIcon className={classes.contactIcon} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="subtitle2" color="textSecondary">
                    Visit Us
                  </Typography>
                }
                secondary={
                  <>
                    <Typography
                      component="span"
                      className={classes.contactLink}
                      sx={{ fontWeight: "bold", display: "block" }}
                    >
                      MOBITILL CENTRE
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 0.5 }}
                    >
                      Enterprise Road Nairobi, next to Rubis Petrol Station at
                      the Junction of Likoni Road and Enterprise Road,
                      Industrial Area
                    </Typography>
                  </>
                }
              />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemAvatar>
                <Avatar className={classes.avatar}>
                  <MailCheckIcon className={classes.contactIcon} />
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Typography variant="subtitle2" color="textSecondary">
                    Email Us
                  </Typography>
                }
                secondary={
                  <Typography
                    component="a"
                    href="mailto:support@mobitill.com"
                    className={classes.contactLink}
                  >
                    support@mobitill.com
                  </Typography>
                }
              />
            </ListItem>
          </List>
        ) : null}
      </Grid>

      <Grid item xs={12} lg={12} sm={12} xl={12} md={12}>
        {isSocialMediaVisible ? <SocialMedia classes={classes} /> : null}
      </Grid>
    </Grid>
  );
};

export default Main;
